import {FC, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {
  Channel2FA,
  getTwoFactorPreview,
  requestTwoFactorCode,
  verifyTwoFactorCode,
} from '../core/_requests'
import {MeasuringBox} from '../core/_models'

// Fluxo:
//   step "choose"  → mostra canais disponiveis (intersecao caixa x operador)
//   step "input"   → input pra digitar o codigo, botao "reenviar" com cooldown
//   step "done"    → callback pra chamada de abertura (com challenge_token)

type Step = 'choose' | 'input'

interface Props {
  show: boolean
  measuringBox: MeasuringBox
  onCancel: () => void
  onVerified: (challengeToken: string) => void
}

const COOLDOWN_SEC = 60

const TwoFactorModal: FC<Props> = ({show, measuringBox, onCancel, onVerified}) => {
  const [step, setStep] = useState<Step>('choose')
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState<{
    email: string | null
    phone: string | null
    has_email: boolean
    has_phone: boolean
  } | null>(null)
  const [channel, setChannel] = useState<Channel2FA | null>(null)
  const [destination, setDestination] = useState<string>('')
  const [code, setCode] = useState('')
  const [cooldown, setCooldown] = useState(0)

  // Carrega preview do operador quando modal abre
  useEffect(() => {
    if (!show) return
    setStep('choose')
    setChannel(null)
    setCode('')
    setDestination('')
    setCooldown(0)
    getTwoFactorPreview()
      .then(setPreview)
      .catch(() => {
        toast.error('Erro ao consultar dados de contato do operador')
        onCancel()
      })
  }, [show, onCancel])

  // Timer de cooldown pro botao "reenviar"
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  if (!show) return null

  const availableChannels: {key: Channel2FA; label: string; contact: string | null}[] = []
  if (measuringBox.send_2fa_email && preview?.has_email) {
    availableChannels.push({key: 'email', label: 'Email', contact: preview.email})
  }
  if (measuringBox.send_2fa_sms && preview?.has_phone) {
    availableChannels.push({key: 'sms', label: 'SMS', contact: preview.phone})
  }
  if (measuringBox.send_2fa_whatsapp && preview?.has_phone) {
    availableChannels.push({key: 'whatsapp', label: 'WhatsApp', contact: preview.phone})
  }

  const noChannelAvailable = preview !== null && availableChannels.length === 0

  const handleSend = async (ch: Channel2FA) => {
    setLoading(true)
    try {
      const r = await requestTwoFactorCode(measuringBox.id as number, ch)
      setChannel(ch)
      setDestination(r.destination)
      setStep('input')
      setCooldown(COOLDOWN_SEC)
      toast.success(`Codigo enviado para ${r.destination}`)
    } catch (err: any) {
      const status = err?.response?.status
      const data = err?.response?.data
      if (status === 429 && data?.retry_after) {
        toast.error(`Aguarde ${data.retry_after}s antes de tentar novamente`)
      } else {
        toast.error(data?.error || 'Erro ao enviar codigo')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Digite os 6 digitos do codigo')
      return
    }
    setLoading(true)
    try {
      const r = await verifyTwoFactorCode(measuringBox.id as number, code)
      onVerified(r.challenge_token)
    } catch (err: any) {
      const data = err?.response?.data
      if (err?.response?.status === 410) {
        toast.error('Codigo expirou. Solicite um novo.')
        setStep('choose')
      } else if (err?.response?.status === 429) {
        toast.error('Tentativas excedidas. Solicite um novo codigo.')
        setStep('choose')
      } else {
        const remaining = data?.attempts_remaining
        toast.error(
          remaining !== undefined
            ? `Codigo invalido. ${remaining} tentativa(s) restante(s).`
            : (data?.error || 'Codigo invalido')
        )
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='modal fade show d-block' style={{background: 'rgba(0,0,0,0.5)'}} tabIndex={-1}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Verificacao em 2 etapas</h5>
            <button type='button' className='btn-close' onClick={onCancel} disabled={loading}></button>
          </div>
          <div className='modal-body'>
            {!preview && <div className='text-center py-3'>Carregando...</div>}

            {noChannelAvailable && (
              <div className='alert alert-warning'>
                Esta caixa exige verificacao em 2 etapas, mas voce nao tem os canais de contato
                necessarios cadastrados no seu perfil. Peca ao administrador para atualizar seu
                cadastro com o email e/ou telefone corretos.
              </div>
            )}

            {step === 'choose' && !noChannelAvailable && preview && (
              <>
                <p className='mb-3'>
                  Caixa CMB nº <b>{measuringBox.cmb_number}</b> exige verificacao. Escolha para
                  onde receber o codigo:
                </p>
                <div className='d-flex flex-column gap-2'>
                  {availableChannels.map((c) => (
                    <button
                      key={c.key}
                      className='btn btn-outline-primary text-start'
                      onClick={() => handleSend(c.key)}
                      disabled={loading}
                    >
                      <b>{c.label}</b>
                      <div className='text-muted small'>{c.contact}</div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {step === 'input' && (
              <>
                <p className='mb-3'>
                  Codigo enviado para <b>{destination}</b> via <b>{channel?.toUpperCase()}</b>.
                  Digite os 6 digitos abaixo:
                </p>
                <input
                  type='text'
                  inputMode='numeric'
                  maxLength={6}
                  className='form-control form-control-lg text-center fs-1 mb-3'
                  style={{letterSpacing: '0.5em'}}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={loading}
                  autoFocus
                />
                <div className='d-flex justify-content-between align-items-center'>
                  <button
                    className='btn btn-link p-0'
                    onClick={() => channel && handleSend(channel)}
                    disabled={loading || cooldown > 0}
                  >
                    {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar codigo'}
                  </button>
                  <button
                    className='btn btn-link p-0 text-muted'
                    onClick={() => setStep('choose')}
                    disabled={loading}
                  >
                    Trocar canal
                  </button>
                </div>
              </>
            )}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' onClick={onCancel} disabled={loading}>
              Cancelar
            </button>
            {step === 'input' && (
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleVerify}
                disabled={loading || code.length !== 6}
              >
                Verificar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export {TwoFactorModal}
